import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { fetchAdminImagePreview } from '@/lib/admin-image-preview';
import { apiJson } from '@/lib/api';
import { formatPacificDateTime } from '@/lib/adminPacificTime';
import type {
  AdminVaultFileRow,
  AdminVaultFilesListResponse,
  AdminVaultMetricsResponse,
} from '@/lib/types';

const PAGE_SIZE = 50;

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function authorLabel(
  author: AdminVaultFilesListResponse['files'][number]['author'],
  userId: number,
): string {
  const n = [author.first_name, author.last_name].filter(Boolean).join(' ').trim();
  return n || `User #${userId}`;
}

function isImageFile(fileType: string): boolean {
  return fileType.toLowerCase().startsWith('image/');
}

function vaultPreviewPath(fileId: number, size: 'thumb' | 'full'): string {
  const params = new URLSearchParams({ size });
  return `/v1/admin/vault/files/${fileId}/image-preview?${params.toString()}`;
}

function AdminVaultImagePreview({
  file,
  onImagePress,
}: {
  file: AdminVaultFileRow;
  onImagePress: (file: AdminVaultFileRow) => void;
}) {
  const { getToken } = useAuth();

  const previewQuery = useQuery({
    queryKey: ['admin', 'vault-preview', file.id, 'thumb'],
    queryFn: () => fetchAdminImagePreview(getToken, vaultPreviewPath(file.id, 'thumb')),
    enabled: isImageFile(file.file_type),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const url = previewQuery.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewQuery.data]);

  if (!isImageFile(file.file_type)) return null;

  if (previewQuery.isLoading) {
    return (
      <div
        className='h-12 w-12 shrink-0 rounded-md border border-gray-200 bg-gray-100 animate-pulse'
        aria-hidden
      />
    );
  }

  if (previewQuery.isError || !previewQuery.data) {
    return (
      <div
        className='flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-1 text-center text-[10px] font-medium text-gray-500'
        title='Preview unavailable'
      >
        No preview
      </div>
    );
  }

  return (
    <button
      type='button'
      onClick={() => onImagePress(file)}
      className='block shrink-0 rounded-md overflow-hidden border border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1'
      title={`Preview ${file.name}`}
    >
      <img
        src={previewQuery.data}
        alt=''
        className='h-12 w-12 object-cover'
      />
    </button>
  );
}

function AdminVaultImageViewer({
  file,
  onClose,
}: {
  file: AdminVaultFileRow;
  onClose: () => void;
}) {
  const { getToken } = useAuth();

  const fullQuery = useQuery({
    queryKey: ['admin', 'vault-preview', file.id, 'full'],
    queryFn: () => fetchAdminImagePreview(getToken, vaultPreviewPath(file.id, 'full')),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const url = fullQuery.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fullQuery.data]);

  return (
    <button
      type='button'
      className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
      onClick={onClose}
      aria-label='Close image'
    >
      <FiX className='absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 text-white' />
      {fullQuery.isLoading ? (
        <div className='h-12 w-12 border-2 border-white border-t-transparent rounded-full animate-spin' />
      ) : fullQuery.data ? (
        <img
          src={fullQuery.data}
          alt={file.name}
          className='max-w-full max-h-full object-contain'
        />
      ) : (
        <p className='text-white text-sm'>Could not load preview.</p>
      )}
    </button>
  );
}

function AdminVaultPage() {
  const { getToken } = useAuth();
  const [page, setPage] = useState(0);
  const [householdFilter, setHouseholdFilter] = useState('');
  const [beneficiaryFilter, setBeneficiaryFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [viewerFile, setViewerFile] = useState<AdminVaultFileRow | null>(null);

  const householdIdParam = householdFilter.trim();
  const householdId =
    householdIdParam && /^\d+$/.test(householdIdParam) ? Number(householdIdParam) : null;
  const beneficiaryIdParam = beneficiaryFilter.trim();
  const beneficiaryId =
    beneficiaryIdParam && /^\d+$/.test(beneficiaryIdParam)
      ? Number(beneficiaryIdParam)
      : null;

  const metricsQuery = useQuery({
    queryKey: ['admin', 'vault', 'metrics'],
    queryFn: () =>
      apiJson<AdminVaultMetricsResponse>(getToken, '/v1/admin/vault/metrics'),
    refetchOnWindowFocus: true,
  });

  const filesQuery = useQuery({
    queryKey: [
      'admin',
      'vault',
      'files',
      page,
      householdIdParam,
      beneficiaryIdParam,
      searchApplied,
      includeDeleted,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (householdId != null) params.set('household_id', String(householdId));
      if (beneficiaryId != null) params.set('beneficiary_id', String(beneficiaryId));
      if (searchApplied.trim()) params.set('q', searchApplied.trim());
      if (includeDeleted) params.set('include_deleted', 'true');
      return apiJson<AdminVaultFilesListResponse>(
        getToken,
        `/v1/admin/vault/files?${params.toString()}`,
      );
    },
    placeholderData: keepPreviousData,
  });

  const metrics = metricsQuery.data;
  const data = filesQuery.data;
  const files = data?.files ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applySearch = () => {
    setSearchApplied(searchQ);
    setPage(0);
  };

  return (
    <div className='space-y-8'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>Vault files</h1>
        <p className='mt-2 text-sm text-gray-600'>
          App-wide document vault usage: storage totals and every uploaded file,
          grouped for browsing with household and child context.
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={() => {
            void metricsQuery.refetch();
            void filesQuery.refetch();
          }}
          className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
        >
          Refresh
        </button>
        {metricsQuery.isFetching || filesQuery.isFetching ? (
          <span className='text-xs text-gray-500'>Updating…</span>
        ) : null}
      </div>

      {metricsQuery.isLoading && !metrics ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : metricsQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load vault metrics.</p>
      ) : metrics ? (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Active files
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {metrics.active_file_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {metrics.deleted_file_count.toLocaleString()} soft-deleted
              </p>
            </div>
            <div className='rounded-lg border border-primary/30 bg-harbor-50 p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Storage (active)
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {formatFileSize(metrics.total_bytes)}
              </p>
              <p className='mt-1 text-xs text-gray-500 tabular-nums'>
                {metrics.total_bytes.toLocaleString()} bytes
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Households · children
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {metrics.household_count.toLocaleString()}
                <span className='text-lg font-normal text-gray-400'> · </span>
                {metrics.beneficiary_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                With at least one active vault file
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                New uploads
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {metrics.uploads_last_7_days.toLocaleString()} / 7d
              </p>
              <p className='mt-1 text-sm tabular-nums text-gray-700'>
                {metrics.uploads_last_30_days.toLocaleString()} in last 30 days
              </p>
            </div>
          </div>

          <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200 space-y-3'>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>All vault files</h2>
                <p className='text-xs text-gray-500 mt-1'>
                  Newest uploads first. Filter by household or child, or search file name /
                  MIME type.
                </p>
              </div>
              <div className='flex flex-wrap gap-4 items-end'>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Household ID
                  </label>
                  <input
                    value={householdFilter}
                    onChange={(e) => {
                      setHouseholdFilter(e.target.value);
                      setPage(0);
                    }}
                    className='px-2 py-1 border border-gray-300 rounded text-sm w-28'
                    placeholder='e.g. 12'
                    inputMode='numeric'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Child ID
                  </label>
                  <input
                    value={beneficiaryFilter}
                    onChange={(e) => {
                      setBeneficiaryFilter(e.target.value);
                      setPage(0);
                    }}
                    className='px-2 py-1 border border-gray-300 rounded text-sm w-28'
                    placeholder='e.g. 5'
                    inputMode='numeric'
                  />
                </div>
                <div className='min-w-[12rem] flex-1 max-w-md'>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Search
                  </label>
                  <div className='flex gap-2'>
                    <input
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') applySearch();
                      }}
                      className='flex-1 px-2 py-1 border border-gray-300 rounded text-sm'
                      placeholder='File name or MIME'
                    />
                    <button
                      type='button'
                      onClick={applySearch}
                      className='px-3 py-1 border border-gray-300 rounded text-sm font-semibold text-gray-700 hover:bg-gray-50'
                    >
                      Apply
                    </button>
                  </div>
                </div>
                <label className='flex items-center gap-2 text-sm text-gray-700 pb-1'>
                  <input
                    type='checkbox'
                    checked={includeDeleted}
                    onChange={(e) => {
                      setIncludeDeleted(e.target.checked);
                      setPage(0);
                    }}
                    className='rounded border-gray-300'
                  />
                  Include deleted
                </label>
              </div>
            </div>

            {filesQuery.isLoading && !data ? (
              <div className='flex justify-center py-16'>
                <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
              </div>
            ) : filesQuery.isError ? (
              <p className='px-4 py-6 text-sm text-red-600'>Could not load vault files.</p>
            ) : files.length === 0 ? (
              <p className='px-4 py-6 text-sm text-gray-500'>No vault files match these filters.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500'>
                    <tr>
                      <th className='px-4 py-2'>File</th>
                      <th className='px-4 py-2'>Child</th>
                      <th className='px-4 py-2'>Household</th>
                      <th className='px-4 py-2'>Uploaded by</th>
                      <th className='px-4 py-2'>Uploaded</th>
                      <th className='px-4 py-2 text-right'>Size</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {files.map((file) => (
                      <tr key={file.id} className='hover:bg-gray-50'>
                        <td className='px-4 py-2 max-w-xs'>
                          <div className='flex items-start gap-3 min-w-0'>
                            <AdminVaultImagePreview
                              file={file}
                              onImagePress={setViewerFile}
                            />
                            <div className='min-w-0'>
                              <p
                                className={`font-medium truncate ${
                                  file.deleted_at
                                    ? 'text-gray-400 line-through'
                                    : 'text-gray-900'
                                }`}
                                title={file.name}
                              >
                                {file.name}
                              </p>
                              <p
                                className='text-xs text-gray-500 truncate'
                                title={file.file_type}
                              >
                                #{file.id} · {file.file_type}
                                {file.deleted_at ? (
                                  <span className='ml-1 text-rose-600 font-medium'>
                                    Deleted
                                  </span>
                                ) : null}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className='px-4 py-2 text-gray-800'>
                          <span className='font-medium'>{file.beneficiary_name}</span>
                          <span className='text-xs text-gray-500 tabular-nums block'>
                            #{file.beneficiary_id}
                          </span>
                        </td>
                        <td className='px-4 py-2 tabular-nums text-gray-700'>
                          #{file.household_id}
                        </td>
                        <td className='px-4 py-2'>
                          <p className='font-medium text-gray-900'>
                            {authorLabel(file.author, file.creator_user_id)}
                          </p>
                          {file.author.email ? (
                            <p
                              className='text-xs text-gray-500 truncate max-w-[14rem]'
                              title={file.author.email}
                            >
                              {file.author.email}
                            </p>
                          ) : null}
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(file.creator_user_id) }}
                            search={{ hid: String(file.household_id) }}
                            className={`${ADMIN_USER_LINK_CLASS} mt-1`}
                          >
                            User profile
                          </Link>
                        </td>
                        <td className='px-4 py-2 text-xs text-gray-600 tabular-nums whitespace-nowrap'>
                          {formatPacificDateTime(file.created_at)}
                          {file.deleted_at ? (
                            <span className='block text-rose-600 mt-0.5'>
                              Deleted {formatPacificDateTime(file.deleted_at)}
                            </span>
                          ) : null}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums text-gray-700 whitespace-nowrap'>
                          {formatFileSize(file.file_size)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {total > 0 ? (
              <div className='flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600'>
                <span className='tabular-nums'>
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE + files.length, total)} of {total}
                </span>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0 || filesQuery.isFetching}
                    className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
                  >
                    Prev
                  </button>
                  <span>
                    Page {page + 1} / {totalPages}
                  </span>
                  <button
                    type='button'
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages - 1 || filesQuery.isFetching}
                    className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {viewerFile ? (
        <AdminVaultImageViewer file={viewerFile} onClose={() => setViewerFile(null)} />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/vault')({
  component: AdminVaultPage,
});
